<template>
  <div ref="filterItemRef" class="filter-item-wrapper">
    <Chip
      class="chip whitespace-nowrap"
      :class="{ 'chip--open': isOpen && !isToggle }"
      :selected="isChipSelected"
      :classes="classes"
      :selected-classes="selectedClasses"
      @click="onClickChip"
    >
      <template #prefix>
        <slot name="prefix" :is-selected="isChipSelected" :is-open="isOpen" />
      </template>
      {{ getLabel?.() || chipLabel || label }}
      <div v-if="!isToggle" class="count">
        {{ selectedOptionsCount || '' }}
      </div>
      <template #suffix>
        <slot name="suffix" />
      </template>
    </Chip>

    <transition name="fade" mode="out-in">
      <div
        v-if="isOpen && !isToggle"
        class="filter-item"
        :class="'filter-item--' + filterKey"
      >
        <div class="filter-item__content">
          <div v-if="dialogTitle" class="label md:hidden font-medium mb-1">
            {{ dialogTitle }}
          </div>
          <div v-if="showSearch" class="search">
            <IconSearch />
            <input
              v-model="filterOptionsSearchTerm"
              type="search"
              :placeholder="placeholder"
              class="search-input"
              @input="handleSearchInput"
            />
          </div>

          <div class="filter-item__options">
            <slot
              :model-value="temporarySelectedOptions"
              name="content"
              :updated="selectTempOptForSlot"
            />

            <div v-for="opt in options" :key="opt.slug">
              <FilterOption
                :selected="temporarySelectedOptions?.includes(opt.slug)"
                :label="opt.name"
                :count="opt.count"
                @click="selectTemporaryOption(opt.slug)"
              />
            </div>
          </div>

          <div
            class="buttons"
            :class="{
              'buttons--hide': isButtonHide,
            }"
          >
            <!-- prettier-ignore-attribute -->
            <Button
              class="button button--cancel"
              variant="text"
              @click="closeFilter"
            >
              {{ btnLabelCancel || $t('search.buttons.cancel') }}
            </Button>
            <!-- prettier-ignore-attribute -->
            <Button
              ref="completeButtonRef"
              class="button button--confirm"
              @click="confirmSelect"
            >
              {{ btnLabelConfirm || $t('search.buttons.confirm') }}
            </Button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import nth from 'lodash/nth'
import { onClickOutside } from '@vueuse/core'
import type { SearchResponse } from '@nuxtjs/algolia'
import type { AlgoliaCity } from '~/composables/types/algolia'
import Chip from '~/components/common/Chip.vue'
import IconSearch from '~/assets/icons/icon-search.svg?component'
import { sendFilterOpen } from '~/composables/useGtmEvents'

const { isMobile } = useDevice()

const emit = defineEmits(['filtersUpdated', 'searchUpdated'])
type filterOption = {
  name: string
  slug: string
  count?: number
}
const props = withDefaults(
  defineProps<{
    isSlide?: boolean
    iconActive?: any
    label: string
    filterKey: string
    multiSelect?: boolean
    classes?: string
    selectedClasses?: string
    options?: string[] | filterOption[]
    placeholder?: string
    showSearch?: boolean
    isToggle?: boolean
    selectedOptions?: string[] | filterOption
    getLabel?: () => string
    dialogTitle?: string
    btnLabelClear?: string
    btnLabelConfirm?: string
    btnLabelCancel?: string
    isButtonHide?: boolean
    mobileLabel?: string
  }>(),
  {
    isSlide: false,
    iconActive: undefined,
    options: () => [],
    placeholder: 'search',
    showSearch: false,
    classes: '',
    selectedClasses: '',
    dialogTitle: '',
    isToggle: false,
    selectedOptions: () => [],
    multiSelect: false,
    getLabel: undefined,
    btnLabelClear: undefined,
    btnLabelConfirm: undefined,
    btnLabelCancel: undefined,
    isButtonHide: false,
    mobileLabel: undefined,
  }
)
const completeButtonRef = ref(null)

const isOpen = ref(false)

const filterOptionsSearchTerm = ref('')
let temporarySelectedOptions = resetTempSelectedOptions()

function handleSearchInput(event: Event) {
  const target = event.target as HTMLInputElement
  emit('searchUpdated', target.value)
}

function resetTempSelectedOptions() {
  if (props.filterKey === 'other') {
    return ref({
      ...props.selectedOptions,
      languages: props.selectedOptions?.languages?.slice(0) || [],
    })
  } else if (props.selectedOptions instanceof Array)
    return ref([...props.selectedOptions])
  return ref(props.selectedOptions)
}

const isFilterOpen = inject('isFilterOpen')

function onClickChip() {
  if (props.isToggle) {
    temporarySelectedOptions = resetTempSelectedOptions()
    const [toggleVal] = temporarySelectedOptions.value
    selectTemporaryOption(!toggleVal)
    return
  }

  if (!isOpen.value) {
    setIsOpen(true)
    sendFilterOpen(props.filterKey)
    isFilterOpen.value = true
  } else {
    confirmSelect()
  }
}

const activeFilterItemId = inject('activeFilterItemId')
const id = ref(`filter-item-${Math.random().toString(36).substr(2, 9)}`)
const filterItemRef = ref(null)

function setIsOpen(value: boolean) {
  activeFilterItemId.value =
    activeFilterItemId.value !== id.value ? id.value : null

  isOpen.value = value
  temporarySelectedOptions = resetTempSelectedOptions()
  if (isMobile) {
    document.body.classList.toggle('prevent-scroll')
  }
}

// Use VueUse onClickOutside for proper Vue-style click-outside handling
onClickOutside(filterItemRef, () => {
  if (isOpen.value) {
    closeFilter()
  }
})

const isChipSelected = computed<boolean>(() => {
  if (props.isToggle) {
    return !!props.selectedOptions?.at?.(0)
  }

  if (Array.isArray(props.selectedOptions)) {
    return !!props.selectedOptions.length
  }

  return !areAllValuesInObjectEmpty(props.selectedOptions)
})

const selectedOptionsCount = computed<number>(() =>
  Array.isArray(props.selectedOptions)
    ? props.selectedOptions?.length
    : countNonEmptyValuesInObject(props.selectedOptions)
)

const localitiesAlgolia = inject('localitiesAlgolia') as ComputedRef<
  SearchResponse<AlgoliaCity>
>

const chipLabel = computed(() => {
  if (props.filterKey === 'city') {
    return localitiesAlgolia.value?.hits?.find(
      (loc) => loc.slug === props.selectedOptions?.at(0)
    )
  }
  return props.label
})

const selectTempOptForSlot = (optionsObj) => {
  if (props.filterKey === 'dates') {
    temporarySelectedOptions.value = [
      optionsObj[0],
      optionsObj[1] ?? optionsObj[0],
    ]
    return
  }

  if (props.filterKey === 'other' && optionsObj.languages) {
    const languages = temporarySelectedOptions.value.languages ?? []

    if (languages.includes(optionsObj.languages)) {
      temporarySelectedOptions.value = {
        ...temporarySelectedOptions.value,
        languages: languages.filter((lang) => lang !== optionsObj.languages),
      }
    } else {
      temporarySelectedOptions.value = {
        ...temporarySelectedOptions.value,
        languages: [...languages, optionsObj.languages],
      }
    }
    return
  }

  if (props.filterKey === 'other') {
    temporarySelectedOptions.value = {
      ...temporarySelectedOptions.value,
      ...optionsObj,
    }
  }
}
const selectTemporaryOption = (optionToSelect) => {
  if (!props.multiSelect) {
    temporarySelectedOptions.value = temporarySelectedOptions.value.includes(
      optionToSelect
    )
      ? []
      : [optionToSelect]
    confirmSelect()
    return
  }
  const optionIndex = temporarySelectedOptions.value?.indexOf(optionToSelect)
  // If filter option is in selected options, remove it. else, add it
  optionIndex !== -1
    ? temporarySelectedOptions.value.splice(optionIndex, 1)
    : temporarySelectedOptions.value.push(optionToSelect)
}
// const clearFilters = () => {
//   temporarySelectedOptions.value = []
//   confirmSelect()
// }
const confirmSelect = () => {
  if (props.filterKey === 'other') {
    emit('filtersUpdated', toRaw(temporarySelectedOptions.value))
  } else {
    const finalSelection = !props.multiSelect
      ? nth((temporarySelectedOptions.value || []).slice(0), 0)
      : (temporarySelectedOptions.value || []).slice(0)

    emit('filtersUpdated', {
      [props.filterKey]: finalSelection,
    })
  }

  closeFilter()
}

const closeFilter = () => {
  setIsOpen(false)
  activeFilterItemId.value = null
  isFilterOpen.value = false
}

watch(
  () => activeFilterItemId.value,
  (newId) => {
    if (newId !== id.value) isOpen.value = false
  }
)
</script>

<style>
body.prevent-scroll {
  overflow: hidden;
}
</style>
<style lang="postcss" scoped>
.chip {
  @apply relative border-gray-200 hover:border-teal-900 hover:text-teal-900
  h-[34px] md:h-[32px] px-3 m-0;

  :deep(.anchor) {
    @apply ease-in-out duration-300;
  }

  &--open {
    @apply border-[#047A88] text-[#047A88] bg-teal-50;

    :deep(.anchor) {
      transform: rotate(-180deg);
    }
  }
}

.count {
  @apply absolute empty:hidden bg-primary inline-block text-white rounded-md
  align-middle text-center top-0 p-1 py-[2px] -mt-2 min-h-1 font-thin
  text-[0.65rem] right-2 leading-3;
}

.filter-item-wrapper {
  @apply border-gray-200 hover:border-teal-900 hover:text-teal-900
  h-[34px] md:h-[32px];
}

.filter-item-wrapper {
  @apply md:relative;
}

.filter-item {
  @apply fixed left-0 right-0 top-0 bottom-0 z-30;

  @apply md:absolute md:top-[calc(100%+8px)] md:right-auto md:bottom-auto md:z-10;

  &__content {
    @apply h-full md:h-auto w-[100vw] md:w-[480px] bg-white rounded-s p-2;

    .filter-item--other & {
      @apply md:w-[640px];
    }

    border-top: 1px solid #e5e7eb;
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.1);
  }

  &__options {
    @apply h-[calc(100%-128px)] overflow-auto overflow-x-hidden;
    @apply md:max-h-[calc(100vh-355px)];
    @apply flex flex-col gap-1 pb-4;

    .filter-item--other & {
      @apply h-[calc(100%-56px)] md:max-h-[calc(100vh-299px)];
    }
  }
}

.search {
  @apply relative mb-4;

  svg {
    @apply absolute top-0 bottom-0 pointer-events-none my-auto;
  }
}

.search-input {
  @apply w-full h-[46px] pl-7 ease-in-out duration-300 outline-none sm:text-xs text-teal-900 bg-white rounded-none;
  border: none;
  border-bottom: 1px solid #047a88;

  &::placeholder {
    @apply text-teal-900;
  }

  &:focus {
    border-color: #02616626;
  }
}

.buttons {
  @apply max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0 max-md:p-2 md:pt-1;
  @apply flex gap-1 bg-white rounded-b-md;

  &--hide {
    @apply md:hidden;
  }

  .button {
    flex: 1 1 100%;

    &--cancel {
      &:hover {
        background: none;
        box-shadow: none;
        text-decoration: none;
      }
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
